<template>
  <article :class="cardClass" class="card card--news">
    <div class="card__inner">
      <MediaCardImage
        :bg-color="mediaBgColor"
        :card-url="pageLink"
        :card-url-title="title"
        :fit="mediaFit"
        :max-width="600"
        :media="media"
        class="media--card"
      />
      <div class="card__content">
        <div>
          <TagsList
            :audiences="audiences"
            :sorting="isDefaultTagsSorting"
            :topics="topics"
            :use-audience-as-primary="isPrimaryAudience"
          />
          <BlockLink v-if="title" :label="title" :link="pageLink">
            <h3 :class="{ card__title: !isFeatureCard }">
              {{ title }}
            </h3>
          </BlockLink>
          <p
            v-if="introduction && !hideIntroduction"
            :class="{ 'visible-sm': isFeatureCard }"
            class="card__intro hidden-xs hidden-sm visible-md"
          >
            {{ trimText(introduction, 120, !isFeatureCard) }}
          </p>
        </div>
        <div class="card__meta">
          <div class="card__meta__group">
            <div class="date">
              <span aria-label="Date" class="icon" role="img">
                <Icon name="fa6-regular:calendar" />
              </span>
              <span>
                {{
                  dateFormat(date, {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })
                }}
              </span>
            </div>
            <div v-if="author" class="author">
              <span aria-label="Author" class="icon" role="img">
                <Icon name="fa6-solid:pen" />
              </span>
              <span>
                {{ author }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { dateFormat, trimText } from '#imports'
import type { CustomCardStoryblok } from '~/types/storyblok'
const props = defineProps<{
  blok?: CustomCardStoryblok
  customStory?: StoryContentBlok
  cardType: string
  hideIntroduction?: boolean
}>()
const storyContent = computed(() => props.blok?.content)
const {
  customLink,
  mediaFit,
  mediaBgColor,
  introduction,
  title,
  media,
  audiences,
  topics,
  isDefaultTagsSorting,
  isPrimaryAudience,
} = useStoryContent({ ...toRefs(props), storyContent })
const pageLink = computed(() => customLink.value || '/' + props.blok?.full_slug)
const isFeatureCard = computed(() => props.cardType === 'featured')
const isVerticalCard = computed(() => props.cardType === 'vertical')
const date = computed(() => props.blok?.first_published_at)
const author = computed(() => props.blok?.author)
const cardClass = computed(() =>
  isFeatureCard.value
    ? 'card--featured'
    : isVerticalCard.value
      ? 'card--vertical card--news-vertical'
      : false,
)
</script>

<style lang="less" scoped>
.card.card--news {
  .card__meta {
    .font-medium();
    .text-body();
  }

  &.card--vertical {
    .card__content {
      min-height: @150px;
    }
  }

  &.card--featured:first-child .card__content {
    border-top: solid @1px @color-fill-divide;

    @media @tablet {
      border: none;
    }
  }

  h3 {
    .font-body();
    .font-medium();
    .font-base();

    @media @tablet {
      .font-lg();
    }
  }
}
</style>
